import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ExternalLink, AlertTriangle } from "lucide-react";

import { tierAllowsByok, tierRequiresByok, tierDisplayName } from "@/lib/tier";
import { supabase } from "@/integrations/supabase/client";
import type { Workspace } from "@/hooks/useWorkspace";
import { ModelPickerCombobox } from "@/components/settings/ModelPickerCombobox";

type Props = { workspace: Workspace };

type JobKind = "composer_chat_turn" | "brief_plan" | "brief_draft";
type ModelOverrides = Partial<Record<JobKind, string>>;

type CredentialRow = {
  provider: string;
  key_preview: string | null;
  model_overrides: ModelOverrides | null;
  created_at: string | null;
  updated_at: string | null;
} | null;

const BYOK_JOB_ROWS: Array<{ key: JobKind; label: string; fallback: string }> = [
  { key: "composer_chat_turn", label: "AI Chat",        fallback: "anthropic/claude-3.5-haiku" },
  { key: "brief_plan",         label: "Planning Agent", fallback: "anthropic/claude-3.5-haiku" },
  { key: "brief_draft",        label: "Writing Agent",  fallback: "anthropic/claude-3.5-sonnet" },
];

/**
 * BYOK OpenRouter API key — the user-pays-OpenRouter rail (Rail 2).
 *
 * Available on every paying tier. Applies to all three configurable AI paths:
 * AI Chat, Planning Agent, and Writing Agent. Does NOT affect Excerptus Credit
 * usage for other AI features (image generation, podcast transcription, etc.)
 * — those always run on Rail 1 (Excerptus Credits). See
 * mem://features/ai-cost-rails.md.
 *
 * - Starter: optional. Without a key, the Writing Agent is unavailable
 *   (Chat + Planning still run on credits).
 * - Plus / Pro: optional. Without a key, all three paths use Excerptus Credits.
 * - Elite / Agency: required. Without a key, ALL three paths are paused.
 */
export const AiProviderSettingsCard = ({ workspace }: Props) => {
  const qc = useQueryClient();
  const tier = workspace.account_tier ?? null;
  const allowed = tierAllowsByok(tier);
  const required = tierRequiresByok(tier);

  const credQuery = useQuery({
    queryKey: ["ai-provider-byok", workspace.id],
    enabled: allowed,
    queryFn: async (): Promise<CredentialRow> => {
      const { data, error } = await supabase.functions.invoke("ai-provider-byok-manage", {
        body: { workspace_id: workspace.id, action: "get" },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      return (data?.credential ?? null) as CredentialRow;
    },
  });

  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [overrides, setOverrides] = useState<ModelOverrides>({});
  const [savingOverrides, setSavingOverrides] = useState(false);

  useEffect(() => {
    setApiKey("");
    setOverrides({});
  }, [workspace.id]);

  useEffect(() => {
    setOverrides((credQuery.data?.model_overrides ?? {}) as ModelOverrides);
  }, [credQuery.data]);

  if (!allowed) {
    return (
      <Card className="shadow-soft">
        <CardHeader><CardTitle>OpenRouter API key (writing models)</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Bringing your own OpenRouter key is not available on the {tierDisplayName(tier)} plan.
        </CardContent>
      </Card>
    );
  }

  const existing = credQuery.data;
  const hasKey = !!existing?.key_preview;

  const save = async () => {
    const trimmed = apiKey.trim();
    if (!trimmed) return toast.error("Paste an OpenRouter key first");
    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke("ai-provider-byok-manage", {
        body: { workspace_id: workspace.id, action: "save", key: trimmed },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      toast.success("OpenRouter key saved");
      setApiKey("");
      qc.invalidateQueries({ queryKey: ["ai-provider-byok", workspace.id] });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm("Remove this OpenRouter key? Advanced writing will fall back to the default routing.")) return;
    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke("ai-provider-byok-manage", {
        body: { workspace_id: workspace.id, action: "delete" },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      toast.success("OpenRouter key removed");
      qc.invalidateQueries({ queryKey: ["ai-provider-byok", workspace.id] });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const saveOverrides = async () => {
    setSavingOverrides(true);
    try {
      const cleaned: ModelOverrides = {};
      for (const row of BYOK_JOB_ROWS) {
        const v = (overrides[row.key] ?? "").trim();
        if (v) cleaned[row.key] = v;
      }
      const { data, error } = await supabase.functions.invoke("ai-provider-byok-manage", {
        body: { workspace_id: workspace.id, action: "save_model_overrides", model_overrides: cleaned },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      toast.success("Model overrides saved");
      qc.invalidateQueries({ queryKey: ["ai-provider-byok", workspace.id] });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSavingOverrides(false);
    }
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle>OpenRouter API key (AI Chat, Planning Agent, Writing Agent)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p className="text-muted-foreground">
          Bring your own OpenRouter key to power the three configurable AI paths:{" "}
          <strong>AI Chat</strong>, the <strong>Planning Agent</strong>, and the{" "}
          <strong>Writing Agent</strong>. With a key, those calls run on your
          OpenRouter account — Excerptus Credits are not used for them. All other
          AI features (image generation, transcription, and any future tools)
          continue to use your monthly Excerptus Credits regardless of whether
          a key is saved here. Your key is verified with OpenRouter, encrypted
          at rest, and only a short preview is ever shown back to you.{" "}
          <a
            href="https://openrouter.ai/keys"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            Get an OpenRouter key <ExternalLink className="h-3 w-3" />
          </a>
        </p>

        {required && !hasKey && (
          <div className="flex items-start gap-2 rounded-md border border-yellow-500/30 bg-yellow-500/10 p-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-yellow-600 dark:text-yellow-400" />
            <div className="space-y-1">
              <p className="font-medium text-foreground">AI Chat, Planning, and Writing are paused</p>
              <p className="text-xs text-muted-foreground">
                The {tierDisplayName(tier)} plan requires your own OpenRouter key
                for AI Chat, the Planning Agent, and the Writing Agent. Add one
                below to enable them.
              </p>
            </div>
          </div>
        )}

        {!required && !hasKey && tier === "starter" && (
          <p className="text-xs text-muted-foreground">
            Optional on the Starter plan. AI Chat and the Planning Agent run on
            Excerptus Credits. The <strong>Writing Agent is only available with
            your own OpenRouter key</strong> on Starter — add one to unlock it,
            or upgrade to Plus to use credits for writing too.
          </p>
        )}

        {!required && !hasKey && tier !== "starter" && (
          <p className="text-xs text-muted-foreground">
            Optional on the {tierDisplayName(tier)} plan. Without your own key,
            AI Chat, the Planning Agent, and the Writing Agent all use your
            monthly Excerptus Credits.
          </p>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="openrouter-key">OpenRouter API key</Label>
          <Input
            id="openrouter-key"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={hasKey ? `Current: ${existing!.key_preview}` : "sk-or-v1-..."}
            autoComplete="off"
            disabled={saving}
          />
          <p className="text-xs text-muted-foreground">
            {hasKey
              ? "Paste a new value to replace the current key. Leave blank to keep it."
              : "Paste your key, then save. It's verified with OpenRouter before being stored."}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={saving || !apiKey.trim()}>
            {saving ? "Saving…" : hasKey ? "Replace key" : "Save key"}
          </Button>
          {hasKey && (
            <Button variant="outline" onClick={remove} disabled={saving}>
              Remove key
            </Button>
          )}
        </div>

        {hasKey && existing?.updated_at && (
          <p className="text-xs text-muted-foreground">
            Last updated {new Date(existing.updated_at).toLocaleString()}
          </p>
        )}

        {hasKey && (
          <div className="space-y-3 border-t pt-4">
            <div>
              <Label className="text-base">Pick models per path</Label>
              <p className="text-xs text-muted-foreground">
                These overrides apply only when the path is actually using your
                OpenRouter key. Excerptus Credit paths and other AI features are
                unaffected. Pick from any current frontier model on OpenRouter,
                or type any model ID (no allowlist). Leave blank to use the
                platform default.
              </p>
            </div>
            <div className="space-y-2">
              {BYOK_JOB_ROWS.map((row) => (
                <div key={row.key} className="grid grid-cols-1 gap-2 sm:grid-cols-[180px_1fr] sm:items-center">
                  <Label htmlFor={`byok-model-${row.key}`} className="font-normal text-sm">
                    {row.label}
                  </Label>
                  <ModelPickerCombobox
                    ariaLabel={`${row.label} model`}
                    catalog="openrouter"
                    value={overrides[row.key] ?? ""}
                    placeholder={row.fallback}
                    disabled={savingOverrides}
                    onChange={(v) => setOverrides((prev) => ({ ...prev, [row.key]: v }))}
                  />
                </div>
              ))}
            </div>
            <Button onClick={saveOverrides} disabled={savingOverrides} variant="outline">
              {savingOverrides ? "Saving…" : "Save model overrides"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
